import dayjs from 'dayjs';

import { propertyTypesWithoutBedBath } from 'src/api/landlord-properties-api';
import type { IProperty } from 'src/services/api/properties/types';

export type IPropertyDetailRow = {
    key: string;
    label: string;
    value: string;
    /** Lease ending within two weeks — worth an agent's attention now. */
    isHighlighted?: boolean;
};

const formatDate = (value: string): string => dayjs(value).format('MMM D, YYYY');

/**
 * How long the property has been sitting, which is the number an agent reacts to. A raw
 * date makes them do the subtraction themselves.
 */
export const formatDaysAgo = (value: string): string => {
    const days = dayjs().startOf('day').diff(dayjs(value).startOf('day'), 'day');
    if (days <= 0) {
        return 'today';
    }
    if (days === 1) {
        return '1 day';
    }
    if (days < 60) {
        return `${days} days`;
    }
    return `${Math.round(days / 30)} months`;
};

/**
 * Bed and bath counts are meaningless for whole buildings, floor plans and rooms, so
 * they are omitted rather than shown as a misleading "0 bd · 0 ba".
 */
export const hasBedBathCounts = (property: IProperty): boolean => (
    property.num_bedrooms != null &&
    property.num_bathrooms != null &&
    (property.type == null || !propertyTypesWithoutBedBath.includes(property.type))
);

export const formatRent = (rent: number | null): string | null => (
    rent == null ? null : `$${rent.toLocaleString()}/mo`
);

export const formatPropertyAddress = (property: IProperty): string => {
    const street = property.unit ?
        `${property.street_address}, Unit ${property.unit}` :
        property.street_address;
    return [street, property.city, [property.state, property.zipcode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
};

/**
 * Condensed one-liner for places too tight for the full row grid: list rows and map
 * tooltips.
 */
export const formatPropertySummary = (property: IProperty): string => {
    const parts: string[] = [];
    if (property.type) {
        parts.push(property.type);
    }
    if (hasBedBathCounts(property)) {
        parts.push(`${property.num_bedrooms} bd · ${property.num_bathrooms} ba`);
    }
    const rent = formatRent(property.rent);
    if (rent) {
        parts.push(rent);
    }
    return parts.join(' · ');
};

/** How soon a lease end counts as "freeing up" and gets called out. */
export const FREEING_SOON_DAYS = 14;

/**
 * The date an occupied property is expected to free up, from its occupancy periods:
 * the end of the period covering today, or failing that the nearest future end date.
 * Null when the property is vacant, has no occupancy data, or the end is unknown —
 * callers should render nothing in that case.
 */
export const getOccupiedUntil = (property: IProperty): string | null => {
    if (property.is_vacant || property.occupancy == null) {
        return null;
    }
    const today = dayjs().startOf('day');
    const candidates = property.occupancy
        .filter((period) => period.end_date != null &&
            !dayjs(period.end_date).startOf('day').isBefore(today))
        .map((period) => period.end_date as string)
        .sort();
    return candidates[0] ?? null;
};

export const isFreeingSoon = (occupiedUntil: string): boolean => (
    dayjs(occupiedUntil).startOf('day').diff(dayjs().startOf('day'), 'day') <= FREEING_SOON_DAYS
);

/** "Jun 3 (in 5 days)" — the countdown is the part an agent actually reacts to. */
export const formatOccupiedUntil = (occupiedUntil: string): string => {
    const days = dayjs(occupiedUntil).startOf('day').diff(dayjs().startOf('day'), 'day');
    const when = days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
    return `${dayjs(occupiedUntil).format('MMM D')} (${when})`;
};

/**
 * Every known attribute of a property worth showing next to a map marker, as ordered
 * label/value pairs. Unknown attributes are dropped instead of rendering an empty row,
 * so a sparse property produces a short card rather than a grid of dashes.
 */
export const getPropertyDetailRows = (property: IProperty): IPropertyDetailRow[] => {
    const rows: IPropertyDetailRow[] = [];

    /*
     * Rent leads and is always present, including when unknown: an agent comparing this
     * against what a lead already turned down needs to see that the number is missing,
     * not be left to assume the row was merely scrolled off.
     */
    rows.push({
        key: 'rent',
        label: 'Rent',
        value: formatRent(property.rent) ?? 'Not set',
    });

    if (hasBedBathCounts(property)) {
        rows.push({
            key: 'beds',
            label: 'Bed / bath',
            value: `${property.num_bedrooms} bd · ${property.num_bathrooms} ba`,
        });
    }

    if (property.square_feet != null) {
        rows.push({
            key: 'square_feet',
            label: 'Size',
            value: `${property.square_feet.toLocaleString()} sq ft`,
        });
    }

    if (property.is_vacant && property.vacant_since) {
        rows.push({
            key: 'vacant_since',
            label: 'Vacant since',
            value: `${formatDate(property.vacant_since)} (${formatDaysAgo(property.vacant_since)})`,
        });
    }

    const occupiedUntil = getOccupiedUntil(property);
    if (occupiedUntil != null) {
        rows.push({
            key: 'occupied_until',
            label: 'Occupied until',
            value: formatOccupiedUntil(occupiedUntil),
            isHighlighted: isFreeingSoon(occupiedUntil),
        });
    }

    if (property.vacant_until) {
        rows.push({
            key: 'vacant_until',
            label: 'Vacant until',
            value: formatDate(property.vacant_until),
        });
    }

    if (property.type) {
        rows.push({ key: 'type', label: 'Type', value: property.type });
    }

    if (property.unit) {
        rows.push({ key: 'unit', label: 'Unit', value: property.unit });
    }

    return rows;
};
